import { useState } from "react";
import { Plus, RefreshCw, Trash2 } from "lucide-react";
import { useAction, useMutation, useQuery } from "convex/react";
import { PageHeader } from "../PageHeader";
import { PolicyTester } from "../shared/PolicyTester";
import { api } from "../../../lib/convexApi";

// Spec 5.1 PR 2 — cut from REST helpers (fetchPolicyPacks /
// updatePolicyPack / createPolicyPack / deletePolicyPack) to Convex
// hooks. The YAML blob now lives in Convex file storage; the page
// renders the metadata list (packId, name, contentHash, ruleCount)
// and offers upload + delete. There is no in-place editor anymore —
// edits land via re-upload, which the action de-duplicates by
// (orgId, packId) and replaces the storage blob atomically.

interface PackRow {
  builtin?: boolean;
  id: string;
  packId: string;
  name: string;
  contentHash: string;
  ruleCount: number;
  uploadedAt: number;
  uploadedBy: string;
}

export function PolicyPacksPage() {
  const data = useQuery(api.policy.queries.packs) as { packs: PackRow[] } | undefined;
  const upload = useAction(api.policy.actions.parseAndStorePack);
  const remove = useMutation(api.policy.mutations.deletePack);
  const reload = useMutation(api.policy.mutations.reload);

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  if (!data) {
    return (
      <>
        <PageHeader title="Policy packs" subtitle="loading…" />
        <div className="sr-page-body">
          <div className="sr-empty">Loading policy packs…</div>
        </div>
      </>
    );
  }

  const packs = data.packs;
  const totalRules = packs.reduce((acc, p) => acc + p.ruleCount, 0);

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const contents = await file.text();
      await upload({ filename: file.name, contents });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
      // Clear the input so re-uploading the same file fires the change event.
      e.target.value = "";
    }
  }

  async function handleDelete(pack: PackRow) {
    if (!window.confirm(`Delete pack "${pack.name}"?`)) return;
    try {
      await remove({ packId: pack.packId });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <>
      <PageHeader
        title="Policy packs"
        subtitle={`${packs.length} packs · ${totalRules} rules total`}
        actions={
          <>
            <button type="button" className="sr-btn sr-btn--ghost" onClick={() => void reload({})}>
              <RefreshCw size={14} /> Reload
            </button>
            <label className="sr-btn">
              <Plus size={14} /> {uploading ? "Uploading…" : "Upload pack"}
              <input
                type="file"
                accept=".yaml,.yml"
                aria-label="Upload pack"
                disabled={uploading}
                onChange={handleUpload}
                style={{ display: "none" }}
              />
            </label>
          </>
        }
      />
      <div className="sr-page-body">
        {uploadError && <div className="sr-empty">⚠ {uploadError}</div>}
        {packs.length === 0 && !uploadError && (
          <div className="sr-empty">
            No policy packs uploaded yet. Use <strong>Upload pack</strong> to ship your first{" "}
            <code>.yaml</code>.
          </div>
        )}
        {packs.length > 0 && (
          <ul className="sr-packs-list">
            {packs.map((p) => (
              <li key={p.id} className="sr-packs-list__item">
                <div>
                  <strong>{p.name}</strong>
                  <span className="sr-pack-chip">{p.ruleCount} rules</span>
                  {/* Built-in packs have no content hash — the backend
                      synthesizes a staleness fingerprint ("7:none",
                      "5:db/drop-ta"), which rendered in a column that reads
                      like a checksum and looked broken. Say what it is. */}
                  {p.builtin ? (
                    <span className="sr-pack-chip">built-in</span>
                  ) : (
                    <code className="sr-pack-hash" title={p.contentHash}>
                      {p.contentHash.slice(0, 12)}
                    </code>
                  )}
                </div>
                {/* Built-in packs ship with the product and cannot be deleted;
                    the button used to render for them and fail on click. */}
                {!p.builtin && (
                  <button
                    type="button"
                    className="sr-btn sr-btn--ghost"
                    aria-label={`Delete ${p.name}`}
                    onClick={() => void handleDelete(p)}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        <div className="sr-packs-tester-pane">
          <PolicyTester />
        </div>
      </div>
    </>
  );
}
